import { createApi, createStore } from "effector";
import { persist } from "effector-storage/session";

import { WizardStore } from "./types";
import { defaultState } from "./utils";

export const $wizard = createStore<WizardStore>(defaultState);

persist<WizardStore>({
  store: $wizard,
  key: "booking-v4-wizard",
  def: defaultState,
});

type WizardStepPayload = {
  service: string;
  step: string;
  steps?: string[];
  valid?: boolean;
  submitting?: boolean;
  submitted?: boolean;
  skip?: boolean;
};

export const wizardApi = createApi($wizard, {
  setValid: (
    state,
    {
      service,
      step,
      valid,
    }: Omit<WizardStepPayload, "submitted" | "submitting" | "skip">,
  ): WizardStore => {
    const isValidService = !!state[service];

    if (!isValidService) {
      return state;
    }

    return {
      ...state,
      [service]: {
        ...state[service],
        [step]: {
          ...state[service][step],
          valid: !!valid,
        },
      },
    };
  },

  setSubmitting: (
    state,
    {
      service,
      step,
      submitting,
    }: Omit<WizardStepPayload, "submitted" | "valid" | "skip">,
  ): WizardStore => {
    return {
      ...state,
      [service]: {
        ...state[service],
        [step]: {
          ...state[service][step],
          submitting: !!submitting,
        },
      },
    };
  },

  resetAllSubmitting: (state): WizardStore => {
    const newState = { ...state };

    Object.keys(newState).forEach((service) => {
      Object.keys(newState[service]).forEach((step) => {
        newState[service][step].submitting = false;
      });
    });

    return newState;
  },

  setSubmitted: (
    state,
    {
      service,
      step,
      submitted,
    }: Omit<WizardStepPayload, "valid" | "submitting" | "skip">,
  ): WizardStore => {
    return {
      ...state,
      [service]: {
        ...state[service],
        [step]: {
          ...state[service][step],
          submitting: false,
          submitted: !!submitted,
        },
      },
    };
  },

  setSkip: (
    state,
    {
      service,
      step,
      skip,
    }: Omit<WizardStepPayload, "valid" | "submitting" | "submitted">,
  ): WizardStore => {
    if (!state?.[service]?.[step]) {
      return state;
    }

    return {
      ...state,
      [service]: {
        ...state[service],
        [step]: {
          ...state[service][step],
          skip: !!skip,
        },
      },
    };
  },

  setBulkSkip: (
    state,
    {
      service,
      steps,
      skip,
    }: Omit<WizardStepPayload, "valid" | "submitting" | "submitted" | "step">,
  ): WizardStore => {
    if (
      !state?.[service] ||
      !steps?.length ||
      steps.some((step) => !state?.[service]?.[step])
    ) {
      return state;
    }
    const newState = { ...state };

    steps.forEach((step) => {
      newState[service][step].skip = !!skip;
    });

    return newState;
  },

  reset: () => {
    return defaultState;
  },
});
